import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { shelfFormSchema } from "./shelfFormSchema";
import Tags from "components/core/blocks/Tags";
import { Button, buttonRecipe } from "components/core/basic/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { errorToast, successToast } from "../../../utils/toasts";
import { useAppSelector } from "../../../redux/hooks";
import { Select } from "../../fields/base/Select";
import { TextField } from "../../fields/base/TextField";
import { Datepicker } from "../../fields/base/Datepicker";
import { css } from "../../../../styled-system/css";
import { inputClassNames } from "../../fields/styles/styles";
import { vstack } from "../../../../styled-system/patterns";
import { Modal } from "../../core/basic/RACModal";

export const marketplaceDict = {
  CA: "Canada",
  USA: "United States",
  MX: "Mexico",
  BR: "Brazil",
  UK: "United Kingdom",
  NL: "Netherlands",
  ES: "Spain",
  IT: "Italy",
  FR: "France",
  IN: "India",
  AE: "UAE",
  DE: "Germany",
  JP: "Japan",
  SG: "Singapore",
  AU: "Australia",
  SE: "Sweden",
  PL: "Poland",
  BE: "Belgium",
  SA: "Saudi Arabia",
  TR: "Turkey",
  IE: "Ireland",
};

const ShelfForm = (props) => {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const starterKeywords = props.data?.searches?.map((search) => search.display || search.value) ?? [];
  if (props.autoKeyword) {
    starterKeywords.push(decodeURI(props.autoKeyword));
  }

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(shelfFormSchema),
    defaultValues: { ...props.data, category: props.data?.category ?? props.categoryFilter, keywords: starterKeywords },
    mode: "onChange",
  });

  const [shelfId, setShelfId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const { data: categoryData } = useCategoriesQuery(marketPlace, "amazon");
  const categoryOptions = useMemo(
    () =>
      categoryData?.map((c) => ({
        label: c.leaf_category_name,
        value: c.id,
      })) ?? [],
    [categoryData],
  );

  useEffect(() => {
    if (!props.create) {
      setShelfId(props.data?.id);
    } else {
      reset({ name: "", description: "", brand_name: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data, props.brandData]);

  // Shelf CRUD requests
  const queryClient = useQueryClient();

  const createShelf = useMutation({
    mutationFn: async (newShelf) => {
      return await api.post("/api/shelves/", newShelf);
    },

    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      if (res.status === 201) {
        if (props.modalForm) {
          props.closeModal();
        }
        props.updateShelfData("create", res.data.id);
        if (props.fullLayout) {
          props.setCategoryFilter(getValues("category"));
        }
        successToast({ message: "Shelf created successfully!" });
      }
    },
    onError: (error) => {
      errorToast({ message: "Failed to create shelf. Please try again.", error });
    },
  });

  const updateShelf = useMutation({
    mutationFn: async (updatedShelf) => {
      return await api.put(`/api/shelves/${shelfId}/`, updatedShelf);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      props.updateShelfData("update");
      successToast({ message: "Shelf updated successfully!" });
    },
    onError: (error) => {
      errorToast({ message: "Failed to update shelf. Please try again.", error });
    },
  });

  const deleteShelf = useMutation({
    mutationFn: async () => {
      return await api.remove(`/api/shelves/${shelfId}/`);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shelves"],
      });
      props.updateShelfData("delete");
    },
  });

  return (
    <>
      {props.numOfShelfCredits <= props.numOfShelves ?
        <VStack paddingX="medium" paddingY="large" gap="large" textAlign={"center"}>
          <p
            className={css({
              fontSize: "xlarge",
              margin: 0,
            })}
          >
            Your subscription plan does not have any more available search analytics shelf credits.
          </p>
          <p
            className={css({
              fontSize: "xlarge",
              margin: 0,
            })}
          >
            To add more, please upgrade your subscription by clicking on the button below.
          </p>
          <Link
            className={buttonRecipe()}
            to={{
              pathname: "/user/billing",
              state: { showModal: "yes", numOfShelves: props.numOfShelves },
            }}
          >
            Upgrade Subscription
          </Link>
        </VStack>
      : <form className={vstack({ gap: "medium", width: "100%", alignItems: "stretch", paddingX: "large" })}>
          <HStack justifyContent="space-between" alignItems="center">
            <TextField label="Marketplace" value={marketplaceDict[marketPlace]} isDisabled />
            <Datepicker
              label="Creation Date"
              value={props.data?.created_on ?? new Date().toISOString()}
              disabled
              inputClassName={css({ width: "100%" })}
            />
          </HStack>
          <Controller
            name={"name"}
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                errorMessage={fieldState.error?.message}
                label={"Shelf Name"}
                placeholder={"Shelf Name"}
              />
            )}
          />
          <Controller
            name={"description"}
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} errorMessage={fieldState.error?.message} label={"Shelf Description"} />
            )}
          />

          <HStack>
            <Controller
              name={"category"}
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  label={"Category"}
                  items={categoryOptions}
                  selectedKey={field.value}
                  errorMessage={fieldState.error?.message}
                  onSelectionChange={(value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
            <Controller
              name={"brand_name"}
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} errorMessage={fieldState.error?.message} label={"Home Brand"} />
              )}
            />
          </HStack>

          <Controller
            name="keywords"
            control={control}
            render={({ field, fieldState }) => {
              const classNames = inputClassNames();
              return (
                <div className={classNames.fieldWrapper}>
                  <label className={classNames.label}>Keywords</label>
                  <Tags
                    modalTitle="Keywords"
                    filledTags={field.value}
                    saveFilledTags={(filledTags) => {
                      field.onChange(filledTags);
                    }}
                  />
                  <div className={classNames.error}>{fieldState.error?.message}</div>
                </div>
              );
            }}
          />
          <HStack justifyContent="space-evenly" gap="medium">
            {props.create && (
              <Button
                variant="secondary"
                onPress={() => {
                  if (props.modalForm) {
                    props.closeModal();
                  }
                }}
              >
                Cancel
              </Button>
            )}

            {props.create ?
              <Button
                isPending={createShelf.isPending}
                onPress={handleSubmit(
                  (values) => {
                    const shelfData = {
                      name: values.name,
                      description: values.description,
                      brand_name: values.brand_name,
                      marketplace: marketPlace,
                      channel: "AMZ",
                      category: values.category,
                      searches: values.keywords.map((keyword) => ({
                        search_type: "SEARCH",
                        value: keyword,
                        display: keyword,
                      })),
                    };

                    createShelf.mutate(shelfData);
                  },
                  (errors) => {
                    errorToast({ message: "Form validation failed. Please check the fields.", error: errors });
                  },
                )}
              >
                Create
              </Button>
            : <Button
                variant={"secondary"}
                isPending={updateShelf.isPending}
                onPress={handleSubmit(
                  (values) => {
                    const shelfData = {
                      name: values.name,
                      description: values.description,
                      brand_name: values.brand_name,
                      marketplace: marketPlace,
                      channel: "AMZ",
                      category: values.category,
                      searches: values.keywords.map((keyword) => ({
                        search_type: "SEARCH",
                        value: keyword,
                        display: keyword,
                      })),
                    };

                    updateShelf.mutate(shelfData);
                  },
                  (errors) => {
                    errorToast({ message: "Form validation failed. Please check the fields.", error: errors });
                  },
                )}
              >
                Update Shelf
              </Button>
            }

            {!props.create && (
              <Button
                onPress={async () => {
                  setShowConfirmModal(true);
                }}
              >
                Delete Shelf
              </Button>
            )}
          </HStack>
        </form>
      }

      <Modal
        isOpen={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title="Are you sure you want to permanently delete this shelf?"
      >
        <HStack justifyContent={"space-evenly"} gap="medium">
          <Button
            variant={"secondary"}
            onPress={() => {
              setShowConfirmModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onPress={() => {
              deleteShelf.mutate();
              setShowConfirmModal(false);
            }}
          >
            Delete
          </Button>
        </HStack>
      </Modal>
    </>
  );
};

export default ShelfForm;
